// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import type { RowData } from "@tanstack/react-table"

export type Alignment = "left" | "center" | "right"

export type SortKey = string | number | null | undefined

export type SortDirection = "asc" | "desc"

export interface TableColumnMeta<TData> {
  width?: number
  align?: Alignment
  headClass?: string
  cellClass?: string
  sortAccessor?: (row: TData) => SortKey
}

declare module "@tanstack/react-table" {
  // Augments TanStack's empty ColumnMeta so a ColumnDef's meta is type-checked
  // against its row type; TValue (the cell value) is not part of this contract.
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface ColumnMeta<TData extends RowData, TValue>
    extends TableColumnMeta<TData> {}
}

export interface ResolvedColumnMeta<TData> {
  width: number | undefined
  align: Alignment
  headClass: string | undefined
  cellClass: string | undefined
  sortAccessor: ((row: TData) => SortKey) | undefined
}

export function defineMeta<TData>(
  meta: TableColumnMeta<TData>,
): TableColumnMeta<TData> {
  return meta
}

function compareKeys(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") return a - b
  const sa = String(a)
  const sb = String(b)
  return sa < sb ? -1 : sa > sb ? 1 : 0
}

const isMissing = (key: SortKey): key is null | undefined =>
  key === null || key === undefined

export function sortWithNullsLast<TData>(
  rows: readonly TData[],
  getKey: (row: TData) => SortKey,
  direction: SortDirection,
): TData[] {
  const sign = direction === "asc" ? 1 : -1
  return rows
    .map((row) => ({ row, key: getKey(row) }))
    .sort((a, b) => {
      const aMissing = isMissing(a.key)
      const bMissing = isMissing(b.key)
      if (aMissing || bMissing) return aMissing === bMissing ? 0 : aMissing ? 1 : -1
      return sign * compareKeys(a.key, b.key)
    })
    .map((entry) => entry.row)
}

export function readMeta<TData>(
  meta: TableColumnMeta<TData> | undefined,
): ResolvedColumnMeta<TData> {
  return {
    width: meta?.width,
    align: meta?.align ?? "left",
    headClass: meta?.headClass,
    cellClass: meta?.cellClass,
    sortAccessor: meta?.sortAccessor,
  }
}
